"use client";

import { useState } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import { cn } from "@/lib/utils";

interface ChatAvatarProps {
  imageUrl?: string | null;
  fallback: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  fallbackClassName?: string;
  children?: React.ReactNode;
  containWideImage?: boolean;
}

/** The avatar box per size; the event media tile fits inside the same box. */
export const CHAT_AVATAR_SIZE: Record<
  NonNullable<ChatAvatarProps["size"]>,
  string
> = {
  sm: "h-8 w-8",
  md: "h-11 w-11",
  lg: "h-14 w-14",
};

const FALLBACK_TEXT_SIZE: Record<
  NonNullable<ChatAvatarProps["size"]>,
  string
> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-lg",
};

const getAvatarColor = (name: string) => {
  // Light: tinted fill with deep text (the old /30 fill + -100 text was
  // pale-on-pale, ~1.3:1). Dark keeps the translucent glass tint.
  const colors = [
    "bg-red-100 text-red-700 dark:bg-red-500/30 dark:text-red-100",
    "bg-blue-100 text-blue-700 dark:bg-blue-500/30 dark:text-blue-100",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/30 dark:text-emerald-100",
    "bg-amber-100 text-amber-800 dark:bg-amber-500/30 dark:text-amber-100",
    "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/30 dark:text-fuchsia-100",
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-100",
    "bg-rose-100 text-rose-700 dark:bg-rose-500/30 dark:text-rose-100",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/30 dark:text-cyan-100",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export function ChatAvatar({
  imageUrl,
  fallback,
  size = "md",
  className,
  fallbackClassName,
  children,
  containWideImage,
}: ChatAvatarProps) {
  const [isWide, setIsWide] = useState(false);
  const fallbackLiquidStyle = !imageUrl ? getAvatarColor(fallback) : "";

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      if (img.naturalWidth > img.naturalHeight) {
        setIsWide(true);
      }
    }
  };

  return (
    <div
      className={cn(
        "relative shrink-0 overflow-hidden rounded-[30%]",
        !imageUrl
          ? `backdrop-blur-2xl ${fallbackLiquidStyle}`
          : "bg-surface-alt shadow-sm",
        CHAT_AVATAR_SIZE[size],
        className,
      )}
    >
      {imageUrl ? (
        <>
          {containWideImage && isWide && (
            <Image
              src={imageUrl}
              alt=""
              fill
              className="pointer-events-none scale-125 object-cover opacity-40 blur-sm select-none"
              sizes={size === "lg" ? "56px" : size === "md" ? "44px" : "32px"}
            />
          )}
          <Image
            src={imageUrl}
            alt={fallback}
            fill
            className={cn(
              containWideImage && isWide
                ? "object-contain p-0.5"
                : "object-cover",
            )}
            onLoad={handleLoad}
            sizes={size === "lg" ? "56px" : size === "md" ? "44px" : "32px"}
          />
        </>
      ) : (
        <div
          className={cn(
            "flex h-full w-full items-center justify-center font-bold",
            FALLBACK_TEXT_SIZE[size],
            fallbackClassName,
          )}
        >
          {fallback.charAt(0).toUpperCase()}
        </div>
      )}
      {children}
    </div>
  );
}
